import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { ContentContainer } from '../components/HeroSectionStyles';
import ParticlesBackground from '../components/ParticlesBackground';

// Page transition animations
const slideInFromRight = keyframes`
  from { opacity: 0; transform: translateX(40px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInFromLeft = keyframes`
  from { opacity: 0; transform: translateX(-40px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideOutToLeft = keyframes`
  from { opacity: 1; transform: translateX(0); }
  to { opacity: 0; transform: translateX(-40px); }
`;

const slideOutToRight = keyframes`
  from { opacity: 1; transform: translateX(0); }
  to { opacity: 0; transform: translateX(40px); }
`;

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  width: 100%;
  padding: 20px;
  position: relative;
  overflow: hidden;

  &.enter-right { animation: ${slideInFromRight} 280ms ease-out both; }
  &.enter-left { animation: ${slideInFromLeft} 280ms ease-out both; }
  &.exit-left { animation: ${slideOutToLeft} 240ms ease-in both; }
  &.exit-right { animation: ${slideOutToRight} 240ms ease-in both; }
`;

const Content = styled(ContentContainer)`
  max-width: 1100px;
  width: 92%;
  margin: 0 auto;
  padding: 48px 56px;
  border: 1px solid rgba(100, 182, 255, 0.25);
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.18);
  box-shadow: 0 0 20px rgba(100, 182, 255, 0.08);
  backdrop-filter: blur(8px);
`;


const SectionTitle = styled.h2`
  font-family: 'Orbitron', sans-serif;
  font-size: 2.6rem;
  color: #64B6FF;
  margin: 1.2rem 0 1.8rem;
  text-align: center;
  letter-spacing: 2px;
  text-shadow: 0 0 12px rgba(100, 182, 255, 0.5);
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 auto;
  max-width: 820px;
`;

const Item = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 14px 0;
  font-size: 1.35rem;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.92);
  letter-spacing: 0.5px;

  &::before {
    content: '›';
    color: #64B6FF;
    font-weight: 700;
    text-shadow: 0 0 10px rgba(100, 182, 255, 0.6);
    margin-top: -2px;
  }
`;

const BackButton = styled.button`
  position: absolute;
  top: 20px;
  left: 20px;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 1px solid rgba(100, 182, 255, 0.4);
  background: rgba(10, 18, 28, 0.6);
  color: #64B6FF;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 0 12px rgba(100, 182, 255, 0.25);
  backdrop-filter: blur(4px);
  transition: transform 120ms ease, box-shadow 200ms ease, background 200ms ease;

  &:hover {
    transform: translateX(-2px);
    box-shadow: 0 0 16px rgba(100, 182, 255, 0.45);
    background: rgba(10, 18, 28, 0.75);
  }
`;

const NextButton = styled(BackButton)`
  left: auto;
  right: 20px;

  &:hover {
    transform: translateX(2px);
  }
`;

function ReuniaoOnboardingObjetivos() {
  const location = useLocation();
  const navigate = useNavigate();

  // Initial animation: if coming from forward (right arrow), enter from right
  const [anim, setAnim] = useState(() => (location.state?.dir === 'back' ? 'enter-left' : 'enter-right'));

  const goBack = () => {
    setAnim('exit-right');
    setTimeout(() => {
      navigate(`/reuniao-onboarding${location.search}`, { state: { dir: 'back' } });
    }, 240);
  };

  const goNext = () => {
    setAnim('exit-left');
    setTimeout(() => {
      navigate(`/reuniao-onboarding/formatos${location.search}`, { state: { dir: 'forward' } });
    }, 240);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') {
        goBack();
      } else if (e.key === 'ArrowRight') {
        goNext();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [location.search]);

  return (
    <>
      <ParticlesBackground />
      <PageWrapper className={anim}>
        <BackButton onClick={goBack} aria-label="Voltar">
          ‹
        </BackButton>
        <NextButton onClick={goNext} aria-label="Avançar">
          ›
        </NextButton>
        <Content>
          <SectionTitle>Objetivos dessa reunião:</SectionTitle>
          <List>
            <Item>Alinhar formatos de Anúncios</Item>
            <Item>Alinhar Script de Atendimento Bianca</Item>
            <Item>Alinhar integrações com CRM</Item>
            <Item>Pegar acessos a Meta, Twilio, Vapi, OpenAi e CRM</Item>
            <Item>Agendar a entrega da implementação</Item>
          </List>
        </Content>
      </PageWrapper>
    </>
  );
}

export default ReuniaoOnboardingObjetivos;
